import React, { useCallback, useContext, useEffect, useRef } from "react";
import ReconnectingWebSocket from "reconnecting-websocket";

import type { PingMessage, PongMessageType } from "../shared/types/pubsub";

import { SocketStatsCollectorContext } from "./SocketStatsCollectorProvider";

import { isProjectorView } from "components/materials/presentation/projector/projector-messaging";
import { store, useAppDispatch, useAppSelector } from "store/index";
import {
  selectCurrentPageId,
  selectCurrentSectionId,
  selectIsSectionVersionOutOfSync,
  selectSectionVersion,
  selectUserIsInactive,
  selectWebsocketSectionId,
} from "store/selectors";
import {
  selectIsObservingVirtualClass,
  selectStableIsMainAvTab,
} from "store/selectors/pubsub-virtual-class-state";
import { selectStoredCollaborationServerVersion } from "store/selectors/view";
import { viewStateActions } from "store/slices/view";
import { isLocalEnv } from "utils/env";
import { logInfoWithDebugFlag } from "utils/logger";
import { usePresentation } from "utils/presentation";
import { getWindowId } from "utils/window";

// TODO: Merge these constants with backend in worksheet constants so they are shared
const TERMINATE_TIMEOUT_MS = 60 * 60000; // 60 minutes
const PROJECTOR_TERMINATE_TIMEOUT_MS = 4 * 60 * 60000; // 4 hours
const PING_INTERVAL_MS = 20000; // 20 seconds (shared by pubsub and sharedb)
const DEBOUNCE_TIME_MS = isLocalEnv ? 0 : 200; // For integration tests we need 0 but otherwise .2 seconds is good
const NORMAL_SOCKET_CLOSE_CODE = 1000; // websocket close code for normal closure
const SERVER_TIMEOUT_MS = 60000; // 60 seconds

export type PingConnectionType = "pubsub" | "sharedb" | "monitoring";

interface PingComponentProps {
  socket: ReconnectingWebSocket;
  connectionType: PingConnectionType;
}

// Unified ping component for both pubsub and ShareDB websockets. Sends pings to keep the
// connection alive and checks that the server responds; if not, reconnects. ShareDB sends
// only a minimal 3-field message; pubsub sends full activity/version payload and handles
// close-on-inactivity and version mismatch. Both use the same 20s interval and coordinate
// on userIsInactive (either stops both; either focus starts both).
export const PingComponent: React.FC<PingComponentProps> = ({ socket, connectionType }) => {
  const lastPingTime = useRef<number>(0);
  const pingInterval = useRef<NodeJS.Timeout | null>(null);
  const closeTimeout = useRef<NodeJS.Timeout | null>(null);

  // Date.now() is cheap; doctor wants a lazy init we don't need here.
  // react-doctor-disable-next-line react-doctor/rerender-lazy-ref-init
  const serverLastResponseRef = useRef(Date.now());
  const currentSectionIdRef = useRef<string | null>(null);
  const pageId = useAppSelector(selectCurrentPageId);

  const isSectionVersionOutOfSync = useAppSelector(selectIsSectionVersionOutOfSync);
  const userIsInactive = useAppSelector(selectUserIsInactive);
  const isObservingClass = useAppSelector(selectIsObservingVirtualClass);
  const isStableMainAvTab = useAppSelector(selectStableIsMainAvTab);
  const isInVirtualClass = isObservingClass || isStableMainAvTab;
  const { isFollowing } = usePresentation();

  const isInVirtualClassRef = React.useRef(isInVirtualClass);

  const dispatch = useAppDispatch();

  // Using ref here to keep the component from unmounting when these values change
  const pageIdRef = useRef<string | null>(pageId);
  const isFollowingRef = useRef<boolean>(isFollowing);

  // Check whether the server is still alive and reconnect if not
  const checkServer = useCallback(() => {
    if (
      serverLastResponseRef.current + SERVER_TIMEOUT_MS < Date.now() &&
      socket.readyState === WebSocket.OPEN &&
      !userIsInactive
    ) {
      logInfoWithDebugFlag(
        `debug_${connectionType}`,
        `Server not responding in ${SERVER_TIMEOUT_MS}ms, reconnecting...`,
      );

      socket.reconnect();
    }
  }, [socket, userIsInactive, connectionType]);

  // Send a ping to the server. ShareDB: minimal 3-field message only. Pubsub: full activity/version payload.
  const sendPing = useCallback(() => {
    if (socket.readyState !== WebSocket.OPEN || userIsInactive) {
      return false;
    }

    if (connectionType === "sharedb") {
      const message = {
        a: "ping",
        windowId: getWindowId(),
        startTime: Date.now(),
      };

      socket.send(JSON.stringify(message));
      return true;
    }

    const currentTime = Date.now();
    const state = store.getState();
    let websocketSectionId = selectWebsocketSectionId(state);
    // TODO: when we don't have a query we pass in "null" as a string in upgrading the websocket
    // we might want to remove the parameter entirely instead, though that could cause other issues.
    // For now, we'll leave it as is, but properly detect it here.
    if (websocketSectionId === "null") websocketSectionId = null;

    // Keep this reference just to ensure we don't send a duplicate auth update
    // when we switch between sections
    const currentSectionId = selectCurrentSectionId(state);

    if (currentTime - lastPingTime.current < DEBOUNCE_TIME_MS) {
      return false;
    }

    const message: PingMessage = {
      a: "ping",
      pageId: pageIdRef.current ?? undefined,
      windowId: getWindowId(),
      active: document.hasFocus() ? "1" : "0",
      following: isFollowingRef.current ? "1" : "0",
      sectionVersion: websocketSectionId === currentSectionId ? selectSectionVersion(state) : 0,
      startTime: Date.now(),
      saveActivity: connectionType !== "monitoring",
    };

    socket.send(JSON.stringify(message));
    lastPingTime.current = currentTime;
    currentSectionIdRef.current = currentSectionId;
    return true;
  }, [socket, userIsInactive, connectionType]);

  // When we don't receive messages from the websocket in sequential order,
  // we know that the section version is out of sync, so we send a ping to
  // the server to receive all the messages from the current section version
  // up to the latest one.
  useEffect(() => {
    if (isSectionVersionOutOfSync && connectionType === "pubsub") {
      sendPing();
      dispatch(viewStateActions.setIsSectionVersionOutOfSync(false));
    }
    // eslint-disable-next-line @minervaproject/no-object-deps/no-object-deps
  }, [dispatch, sendPing, isSectionVersionOutOfSync, connectionType]);

  // Stop pinging the server
  const stopPingInterval = useCallback(() => {
    if (pingInterval.current !== null) {
      clearInterval(pingInterval.current);
    }
    pingInterval.current = null;
  }, []);

  // Stop waiting 30 minutes to close the socket
  const stopCloseTimeout = useCallback(() => {
    logInfoWithDebugFlag(
      `debug_${connectionType}`,
      "Stopping close socket timeout due to user activity",
    );
    if (closeTimeout.current !== null) {
      clearTimeout(closeTimeout.current);
    }
    closeTimeout.current = null;
  }, [connectionType]);

  // Start pinging the server every 30 seconds and checking if the server responsds
  const startPingInterval = useCallback(
    (inactive = userIsInactive) => {
      if (inactive) {
        return;
      }

      // If we started to close due to inactivity, stop closing
      // but only if the tab is active.  This can happen if
      // we immediate leave the tab after receiving focus
      if (document.hasFocus()) {
        stopCloseTimeout();
      }

      // Check that we didn't get disconnected
      if (socket.readyState !== WebSocket.OPEN && socket.readyState !== WebSocket.CONNECTING) {
        logInfoWithDebugFlag(
          `debug_${connectionType}`,
          "Socket not open, reconnecting before starting ping interval",
        );
        socket.reconnect();
      }

      sendPing();

      // Only start the interval if we sent a ping or there isn't one already running
      if (!pingInterval.current) {
        // Start our server check timer
        serverLastResponseRef.current = Date.now();

        // Make sure we clear any existing one
        stopPingInterval();

        pingInterval.current = setInterval(() => {
          checkServer();
          sendPing();
        }, PING_INTERVAL_MS);
      }
    },
    [
      socket,
      sendPing,
      stopPingInterval,
      stopCloseTimeout,
      checkServer,
      userIsInactive,
      connectionType,
    ],
  );

  // Start the wait process to close the socket if the user is inactive
  const startCloseSocketTimeout = useCallback(() => {
    sendPing();

    if (closeTimeout.current) {
      return;
    }

    if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
      const terminateSocketTimeoutMS = isProjectorView()
        ? PROJECTOR_TERMINATE_TIMEOUT_MS
        : TERMINATE_TIMEOUT_MS;

      closeTimeout.current = setTimeout(() => {
        if (isInVirtualClassRef.current) {
          logInfoWithDebugFlag(
            `debug_${connectionType}`,
            "Not closing socket due to virtual class presence",
          );
          // Don't close the socket if we're in a virtual class
          return;
        }

        if (socket.readyState === WebSocket.OPEN) {
          socket.close(NORMAL_SOCKET_CLOSE_CODE, "User inactive");
          dispatch(viewStateActions.setUserIsInactive(true));

          logInfoWithDebugFlag(`debug_${connectionType}`, "Closing socket due to user inactivity", {
            sectionId: selectCurrentSectionId(store.getState()),
            websocketSectionId: selectWebsocketSectionId(store.getState()),
            windowId: getWindowId(),
            pageId: pageIdRef.current,
          });
        }

        // Closed socket so we can stop pinging
        stopPingInterval();
      }, terminateSocketTimeoutMS);
    }
  }, [socket, stopPingInterval, sendPing, dispatch, connectionType]);

  // When user is marked inactive (by either ping component), stop our ping interval
  useEffect(() => {
    if (userIsInactive) {
      stopPingInterval();
    }

    // eslint-disable-next-line @minervaproject/no-object-deps/no-object-deps
  }, [userIsInactive, stopPingInterval]);

  // When user is marked active (e.g. other component received focus), start our ping interval if socket is open
  useEffect(() => {
    if (
      !userIsInactive &&
      (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)
    ) {
      startPingInterval(false);
    }
    // eslint-disable-next-line @minervaproject/no-object-deps/no-object-deps
  }, [userIsInactive, socket, startPingInterval]);

  // If the page changes send an immediate ping
  // or if the user's following status changes
  // or we need an auth update
  useEffect(() => {
    if (pageIdRef.current !== pageId || isFollowingRef.current !== isFollowing) {
      pageIdRef.current = pageId;
      isFollowingRef.current = isFollowing;
      sendPing();
    }
    // eslint-disable-next-line @minervaproject/no-object-deps/no-object-deps
  }, [pageId, isFollowing, sendPing]);

  const socketStatsCollector = useContext(SocketStatsCollectorContext);

  // socket listeners
  useEffect(() => {
    const handleWebsocketMessage = (event: { data: string }) => {
      try {
        const message = JSON.parse(event.data) as PongMessageType;

        serverLastResponseRef.current = Date.now();

        if (message.a === "pong") {
          if (connectionType !== "monitoring" && message.startTime && socketStatsCollector) {
            socketStatsCollector.addPingRecord(connectionType, Date.now() - message.startTime);
          }

          if (connectionType === "pubsub") {
            // Handle version mismatch detection via pong messages (pubsub only)
            const state = store.getState();
            const currentStoredVersion = selectStoredCollaborationServerVersion(state);
            const shouldShowModal = message.forceReloadModal === true;

            if (currentStoredVersion === null) {
              // First time receiving version - store it, no comparison
              dispatch(viewStateActions.setStoredCollaborationServerVersion(message.serverVersion));
            } else if (currentStoredVersion !== message.serverVersion) {
              // Stored version mismatches server version - version change detected
              // Don't update stored version - keep it as original so modal/banner stays visible
              if (shouldShowModal) {
                // Show force reload modal
                dispatch(viewStateActions.setShowForceReloadModal(true));
                dispatch(viewStateActions.setShowVersionMismatchWarning(false));
              } else {
                // Show banner instead
                dispatch(viewStateActions.setShowForceReloadModal(false));
                dispatch(viewStateActions.setShowVersionMismatchWarning(true));
              }
            } else {
              dispatch(viewStateActions.setShowVersionMismatchWarning(false));
              dispatch(viewStateActions.setShowForceReloadModal(false));
            }
          }
        }
      } catch {
        // Other listeners will handle error checking
      }
    };

    const handleOpen = () => {
      if (!document.hasFocus()) startCloseSocketTimeout();

      startPingInterval();
    };

    socket.addEventListener("open", handleOpen);
    socket.addEventListener("message", handleWebsocketMessage);
    socket.addEventListener("close", stopPingInterval);

    return () => {
      // remove all intervals and listeners
      stopPingInterval();
      stopCloseTimeout();

      socket.removeEventListener("open", handleOpen);
      socket.removeEventListener("message", handleWebsocketMessage);
      socket.removeEventListener("close", stopPingInterval);
    };
    /* eslint-disable @minervaproject/no-object-deps/no-object-deps */
  }, [
    socket,
    connectionType,
    stopPingInterval,
    startPingInterval, // react-doctor-disable-line react-doctor/prefer-use-effect-event
    stopCloseTimeout,
    dispatch,
    socketStatsCollector,
    startCloseSocketTimeout, // react-doctor-disable-line react-doctor/prefer-use-effect-event
  ]);
  /* eslint-enable @minervaproject/no-object-deps/no-object-deps */

  // window listeners
  useEffect(() => {
    const handleFocus = () => {
      dispatch(viewStateActions.setUserIsInactive(false));

      startPingInterval(false);
    };

    window.addEventListener("focus", handleFocus);
    window.addEventListener("blur", startCloseSocketTimeout);

    return () => {
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("blur", startCloseSocketTimeout);
    };

    // react-doctor-disable-next-line react-doctor/prefer-use-effect-event
  }, [startPingInterval, startCloseSocketTimeout, dispatch]);

  useEffect(() => {
    isInVirtualClassRef.current = isInVirtualClass;
    if (!isInVirtualClass && !document.hasFocus()) {
      startCloseSocketTimeout();
    }
    // We only want to run this when isInVirtualClass changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInVirtualClass]);

  return null;
};
